import React, { useMemo } from 'react';
import { Form } from '@ant-design/compatible';

import InputForm from '../../../../components/form/InputForm';
import SelectForm from '../../../../components/form/SelectForm';
import Button from '../../../../components/Button';
import useCategorySearchFilter from '../../../../hooks/category/useCategorySearchFilter';
import {
  CATEGORY_SEARCH_KEYWORD_TYPE,
  CATEGORY_SEARCH_KEYWORD_TYPE_SELECT_LIST,
  CATEGORY_SEARCH_MAIN_TYPE,
  CATEGORY_SEARCH_MAIN_TYPE_SELECT_LIST,
} from '../../../../constants/service/app/categoryConstants';
import './CategorySearchBox.scss';

const CategorySearchBox = (props) => {
  const { form, filterOption } = props;

  const { optionListKeyMap, onChangeKeywordOption, isDisabled, onSubmit, onReset } = useCategorySearchFilter(
    form,
    filterOption,
  );

  // 카테고리 검색 1차 옵션 id
  const mainOptionId = useMemo(() => {
    // 검색 1차 필터 옵션 값을 변경하지 않았고, 현재 해당 값이 선택하지 않은 값으로 되어있을 경우(마운트 시 초깃값 설정)
    if (!form.isFieldTouched('main-option') && form.getFieldValue('main-option') === CATEGORY_SEARCH_MAIN_TYPE.ENTIRE) {
      // 카테고리 종류
      if (filterOption.type) return CATEGORY_SEARCH_MAIN_TYPE.KIND;
      // 카테고리 활성 여부
      else if (filterOption.status) return CATEGORY_SEARCH_MAIN_TYPE.STATUS;
      // 카테고리 전시 여부
      else if (filterOption.isExhibit) return CATEGORY_SEARCH_MAIN_TYPE.EXHIBIT;
      // 카테고리 노출 위치
      else if (filterOption.exposureType) return CATEGORY_SEARCH_MAIN_TYPE.EXPOSE;
      // 카테고리 상품 정렬 순
      else if (filterOption.sortType) return CATEGORY_SEARCH_MAIN_TYPE.SORT;
      // 아무 옵션도 선택하지 않았을 때
      else return CATEGORY_SEARCH_MAIN_TYPE.ENTIRE;
    }

    // 검색 1차 필터 옵션 값을 변경했을 경우
    return form.getFieldValue('main-option');
  }, [form.isFieldTouched('main-option'), form.getFieldValue('main-option'), filterOption]);

  /**
   * 검색 카테고리 키워드 타입 초기값
   * @type {number}
   */
  const searchKeywordTypeInitialValue = useMemo(() => {
    // 카테고리 id
    if (filterOption.id) return CATEGORY_SEARCH_KEYWORD_TYPE.ID;
    // 카테고리 이름
    else if (filterOption.name) return CATEGORY_SEARCH_KEYWORD_TYPE.NAME;
    // 선택하지 않았을 때
    else return CATEGORY_SEARCH_KEYWORD_TYPE.NONE;
  }, [filterOption]);

  /**
   * 검색 카테고리 키워드 타입 초기값
   * @type {number}
   */
  const searchKeywordInitialValue = useMemo(() => {
    // 검색한 카테고리 id
    if (filterOption.id) return filterOption.id;
    // 검색한 카테고리 이름
    else if (filterOption.name) return filterOption.name;
    // 검색 키워드를 입력하지 않았을 때
    else return null;
  }, [filterOption]);

  /**
   * 검색 1차 필터 옵션 초기값
   * @type {number} - 검색 1차 필터 옵션 값
   */
  const searchMainOptionInitialValue = useMemo(() => {
    // 카테고리 종류
    if (filterOption.type) return CATEGORY_SEARCH_MAIN_TYPE.KIND;
    // 카테고리 활성 여부
    else if (filterOption.status) return CATEGORY_SEARCH_MAIN_TYPE.STATUS;
    // 카테고리 전시 여부
    else if (filterOption.isExhibit) return CATEGORY_SEARCH_MAIN_TYPE.EXHIBIT;
    // 카테고리 노출 위치
    else if (filterOption.exposureType) return CATEGORY_SEARCH_MAIN_TYPE.EXPOSE;
    // 카테고리 상품 정렬 순
    else if (filterOption.sortType) return CATEGORY_SEARCH_MAIN_TYPE.SORT;
    // 아무 옵션도 선택하지 않았을 때
    else return CATEGORY_SEARCH_MAIN_TYPE.ENTIRE;
  }, [filterOption]);

  /**
   * 검색 2차 필터 옵션 초기값
   * @type {number} - 검색 2차 필터 옵션 값
   */
  const searchSubOptionInitialValue = useMemo(() => {
    // 카테고리 종류
    if (filterOption.type) return Number(filterOption.type);
    // 카테고리 활성 여부
    else if (filterOption.status) return Number(filterOption.status);
    // 카테고리 전시 여부
    else if (filterOption.isExhibit) return Number(filterOption.isExhibit);
    // 카테고리 노출 위치
    else if (filterOption.exposureType) return Number(filterOption.exposureType);
    // 카테고리 상품 정렬 순
    else if (filterOption.sortType) return Number(filterOption.sortType);
    // 아무 옵션도 선택하지 않았을 때
    else return CATEGORY_SEARCH_MAIN_TYPE.ENTIRE;
  }, [filterOption]);

  return (
    <div className="category-search-box-wrap">
      <Form
        className="category-search-box-form-wrap"
        onKeyUp={(e) => {
          if (e.code !== 'Enter') return;
          /**
           * 엔터키를 눌렀을 때도 검색 처리
           */
          onSubmit();
        }}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="category-search-left-section">
          <SelectForm
            isTable
            form={form}
            initialValue={searchKeywordTypeInitialValue}
            onChange={(type) => {
              onChangeKeywordOption(type);
            }}
            list={CATEGORY_SEARCH_KEYWORD_TYPE_SELECT_LIST}
            name="keyword-option"
          />
          <InputForm
            form={form}
            initialValue={searchKeywordInitialValue}
            name="keyword"
            placeholder="검색 키워드를 입력해주세요"
            disabled={isDisabled}
          />
        </div>
        <div className="category-search-right-section">
          <SelectForm
            isTable
            form={form}
            initialValue={searchMainOptionInitialValue}
            list={CATEGORY_SEARCH_MAIN_TYPE_SELECT_LIST}
            name="main-option"
          />
          <SelectForm
            isTable
            form={form}
            initialValue={searchSubOptionInitialValue}
            list={optionListKeyMap[mainOptionId || CATEGORY_SEARCH_MAIN_TYPE.ENTIRE]}
            name="sub-option"
          />
        </div>
        <div className="category-search-btn-section">
          <Button color="primary" isRadius text="검색" />
          <Button isRadius text="초기화" onClick={onReset} />
        </div>
      </Form>
    </div>
  );
};

export default Form.create()(CategorySearchBox);
